/**
 * Displays a single extracted term insurance policy.
 *
 * Layout (per product spec):
 *   Section 1 — Protection snapshot         (always visible: cover · status · till age · premium)
 *   Section 2 — Nominees & family           (always visible: who receives the money + share)
 *   Section 3 — Policy held by              (always visible: the customer who owns it)
 *   ----- Show more -----
 *   Section 4 — Premium status              (next due, auto-debit, policy term, paying term)
 *   Section 5 — Emergency claim section     (insurer, policy #, helpline, claims email)
 *   Section 6 — What to know                (standard regulatory disclosures)
 *   Section 7 — Policyholder vs life-assured / extraction notes
 *
 * Top 5 are what a family member must see at a glance during a claim:
 *   1. Cover amount   2. Policy status (active/lapsed)   3. Valid-till-age
 *   4. Nominees + share   5. Riders
 */
import * as React from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
} from '@finmin/ui';
import { useAuth } from '@finmin/auth';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Info,
  Mail,
  Phone,
  Shield,
  ShieldAlert,
  User,
  Users,
  XCircle,
} from 'lucide-react';
import type { TermInsurancePolicyExtracted } from '@/api/profile-documents';
import {
  formatDate,
  formatFrequency,
  formatINR,
  formatINRCompact,
  formatPolicyStatus,
  formatVariant,
} from './format';

interface Props {
  policy: TermInsurancePolicyExtracted;
  /** Open details on first render. Default false. */
  defaultExpanded?: boolean;
}

export function TermPolicyCard({ policy: p, defaultExpanded = false }: Props) {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const { user } = useAuth();

  const headlineTitle = p.insurer_name ?? 'Term insurance policy';
  const subtitle = [p.plan_name, p.policy_number && `#${p.policy_number}`]
    .filter(Boolean)
    .join(' · ');

  const status = formatPolicyStatus(p.policy_status);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
              <span className="truncate">{headlineTitle}</span>
            </CardTitle>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1 truncate">{subtitle}</p>
            )}
          </div>
          {p.is_manually_edited && (
            <Badge variant="outline" className="shrink-0 text-xs">
              Edited
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* ── Section 1 — Protection Snapshot ───────────────────────────── */}
        <section>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
            <Stat
              label="Life cover"
              value={formatINRCompact(p.sum_assured)}
              sub={p.variant ? formatVariant(p.variant) : undefined}
            />
            <Stat
              label="Status"
              value={
                <StatusPill tone={status.tone}>{status.label}</StatusPill>
              }
            />
            <Stat
              label="Cover till"
              value={p.valid_till_age ? `Age ${p.valid_till_age}` : '—'}
              sub={p.term_years ? `${p.term_years}-yr term` : undefined}
            />
            <Stat
              label="Premium"
              value={formatINRCompact(p.premium_amount)}
              sub={formatFrequency(p.premium_frequency)}
            />
          </div>
        </section>

        {/* ── Section 2 — Nominees & Family ────────────────────────────── */}
        {p.nominees.length > 0 && (
          <section className="rounded-md border border-border bg-secondary/30 p-3">
            <Label>
              <Users className="h-3 w-3 inline mr-1" aria-hidden />
              Who receives the claim
            </Label>
            <ul className="mt-2 space-y-1.5">
              {p.nominees.map((n, i) => (
                <li
                  key={`${n.name ?? 'nominee'}-${i}`}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-foreground min-w-0 truncate">
                    <span className="font-medium">{n.name ?? '—'}</span>
                    {n.relationship && (
                      <span className="text-muted-foreground"> · {n.relationship}</span>
                    )}
                    {n.is_minor && (
                      <Badge variant="outline" className="ml-2 text-xs">
                        minor
                        {n.appointee_name ? ` · via ${n.appointee_name}` : ''}
                      </Badge>
                    )}
                  </span>
                  <span className="text-sm font-semibold text-foreground tabular-nums ml-2 shrink-0">
                    {n.share_pct != null ? `${n.share_pct}%` : '—'}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ── Section 3 — Policy held by ────────────────────────────────── */}
        <section className="flex items-center gap-2 text-xs text-muted-foreground">
          <User className="h-3.5 w-3.5" aria-hidden />
          <span>
            Policy held by{' '}
            <span className="text-foreground font-medium">
              {p.policyholder_name ?? user?.name ?? 'you'}
            </span>
          </span>
          <Badge variant="outline" className="ml-1 text-xs">
            owner
          </Badge>
        </section>

        {/* ── Riders (top 5 — chip strip if any) ────────────────────────── */}
        {p.riders.length > 0 && (
          <section>
            <Label>Riders attached</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {p.riders.map((r) => (
                <Badge key={r} variant="secondary">
                  {r}
                </Badge>
              ))}
            </div>
          </section>
        )}

        {/* ── Show-more details ─────────────────────────────────────────── */}
        {expanded && (
          <div className="space-y-5 border-t border-border pt-5">
            {/* Section 4 — Premium status */}
            <section>
              <Label>Premium status</Label>
              <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
                <Mini
                  label="Next due"
                  value={formatDate(p.next_premium_due_date)}
                />
                <Mini
                  label="Auto-debit"
                  value={
                    p.has_auto_debit === true
                      ? 'Active'
                      : p.has_auto_debit === false
                        ? 'Not set'
                        : '—'
                  }
                />
                <Mini
                  label="Paying term"
                  value={
                    p.premium_paying_term ? `${p.premium_paying_term} yrs` : '—'
                  }
                />
                <Mini label="Matures" value={formatDate(p.maturity_date)} />
              </dl>
            </section>

            {/* Section 5 — Emergency claim section */}
            <section className="rounded-md border-2 border-destructive/20 bg-destructive/5 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-destructive" aria-hidden />
                <p className="text-sm font-semibold text-foreground">
                  Emergency claim section
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                If something happens, this is what you'll need.
              </p>

              <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2 mt-2">
                <Mini
                  label="Insurer"
                  value={p.insurer_name ?? '—'}
                />
                <Mini
                  label="Policy number"
                  value={p.policy_number ?? '—'}
                />
              </dl>

              <div className="space-y-1.5 pt-1">
                {p.claim_helpline && (
                  <ContactLine
                    icon={<Phone className="h-3.5 w-3.5 text-destructive" />}
                    href={`tel:${p.claim_helpline}`}
                    value={p.claim_helpline}
                    meta="24x7 claim helpline"
                  />
                )}
                {p.customer_care && p.customer_care !== p.claim_helpline && (
                  <ContactLine
                    icon={<Phone className="h-3.5 w-3.5 text-muted-foreground" />}
                    href={`tel:${p.customer_care}`}
                    value={p.customer_care}
                    meta="Customer care"
                  />
                )}
                {p.claims_email && (
                  <ContactLine
                    icon={<Mail className="h-3.5 w-3.5 text-muted-foreground" />}
                    href={`mailto:${p.claims_email}`}
                    value={p.claims_email}
                    meta="Claims email"
                  />
                )}
              </div>
            </section>

            {/* Section 6 — Standard disclosures (hardcoded boilerplate) */}
            <RegulatorySummary />

            {/* Section 7 — Policyholder vs life-assured + extraction notes */}
            {p.policyholder_name &&
              p.life_assured_name &&
              p.policyholder_name !== p.life_assured_name && (
                <section>
                  <Label>Cover applies to</Label>
                  <dl className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Mini
                      label="Policyholder"
                      value={p.policyholder_name}
                    />
                    <Mini
                      label="Life assured"
                      value={p.life_assured_name}
                    />
                  </dl>
                </section>
              )}

            {p.extraction_notes && (
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
                <p>{p.extraction_notes}</p>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Life cover: <span className="font-medium">{formatINR(p.sum_assured)}</span>{' '}
              · Extracted {formatDate(p.extracted_at)}
            </p>
          </div>
        )}

        {/* ── Toggle ─────────────────────────────────────────────────────── */}
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded((v) => !v)}
            className="gap-1.5 text-xs"
          >
            {expanded ? (
              <>
                Show less
                <ChevronUp className="h-3 w-3" aria-hidden />
              </>
            ) : (
              <>
                Show more
                <ChevronDown className="h-3 w-3" aria-hidden />
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </p>
      <div className="text-sm font-semibold text-foreground mt-0.5 tabular-nums">
        {value}
      </div>
      {sub && (
        <p className="text-xs text-muted-foreground mt-0.5 truncate">{sub}</p>
      )}
    </div>
  );
}

function StatusPill({
  tone,
  children,
}: {
  tone: 'success' | 'warning' | 'destructive' | 'muted';
  children: React.ReactNode;
}) {
  const Icon =
    tone === 'success' ? CheckCircle2 : tone === 'destructive' ? XCircle : Clock;
  const toneClass: Record<typeof tone, string> = {
    success: 'bg-success/10 text-success border-success/20',
    warning: 'bg-warning/10 text-warning border-warning/30',
    destructive: 'bg-destructive/10 text-destructive border-destructive/20',
    muted: 'bg-secondary text-muted-foreground border-border',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        toneClass[tone],
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {children}
    </span>
  );
}

function Mini({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium text-foreground">{value}</dd>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
      {children}
    </p>
  );
}

function ContactLine({
  icon,
  href,
  value,
  meta,
}: {
  icon: React.ReactNode;
  href: string;
  value: string;
  meta?: string;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {icon}
      <a href={href} className="font-medium hover:underline">
        {value}
      </a>
      {meta && <span className="text-xs text-muted-foreground">{meta}</span>}
    </div>
  );
}

/**
 * Standard regulatory disclosures that apply to every Indian term insurance
 * policy under IRDAI rules. Hardcoded because they're identical across
 * insurers and "extracting" them from each PDF would be wasteful.
 */
function RegulatorySummary() {
  return (
    <section className="rounded-md bg-secondary/40 p-3 space-y-2">
      <div className="flex items-center gap-1.5">
        <AlertTriangle className="h-3.5 w-3.5 text-warning" aria-hidden />
        <Label>What to know</Label>
      </div>
      <ul className="space-y-1.5 text-xs text-muted-foreground list-disc pl-5">
        <li>
          <span className="text-foreground">Suicide exclusion (first year)</span> —
          if death is by suicide within 12 months of policy start (or revival),
          the insurer typically refunds 80% of premiums paid instead of the full
          sum assured.
        </li>
        <li>
          <span className="text-foreground">3-year incontestability window</span> —
          insurers can challenge a claim within the first 3 years if material
          facts (medical history, income, lifestyle) were misstated. Beyond 3
          years, claims are largely incontestable.
        </li>
        <li>
          <span className="text-foreground">Premium lapse stops cover</span> —
          missing a premium past the grace period (usually 15–30 days) makes the
          policy lapse and claims may be denied. Revive within the revival
          window if missed.
        </li>
      </ul>
    </section>
  );
}
